import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  ComponentProps,
  useEffect,
} from "react";
import { cn } from "@/lib/utils.ts";
import { atom } from "xoid";
import { useAtom } from "@xoid/react";

interface ModalContextType {
  showModal: <T>(
    modal: ReactNode,
    className?: string,
  ) => Promise<T | undefined>;
  closeModal: (result?: any) => void;
  closeAllModals: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

const shouldHideModalOnOutsideClickAtom = atom(true);

export function useDisableModalHideOnOutsideClick(opts: {
  shouldHideModalOnOutsideClick: boolean | undefined;
}) {
  useEffect(() => {
    if (opts.shouldHideModalOnOutsideClick === undefined) return;
    shouldHideModalOnOutsideClickAtom.set(opts.shouldHideModalOnOutsideClick);
    if (!opts.shouldHideModalOnOutsideClick) {
      return () => {
        shouldHideModalOnOutsideClickAtom.set(true);
      };
    }
  }, [opts.shouldHideModalOnOutsideClick]);
}

interface ModalEntry {
  content: ReactNode;
  className?: string;
  resolve: (value: unknown) => void;
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalStack, setModalStack] = useState<ModalEntry[]>([]);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const shouldHideModalOnOutsideClick = useAtom(
    shouldHideModalOnOutsideClickAtom,
  );

  const showModal = <T,>(
    modalContent: ReactNode,
    className?: string,
  ): Promise<T | undefined> => {
    return new Promise((resolve) => {
      setModalStack((prev) => [
        ...prev,
        { content: modalContent, resolve, className } as ModalEntry,
      ]);
    });
  };

  const closeModal = (result?: any) => {
    setModalStack((prev) => {
      const newStack = prev.slice(0, -1); // Remove the topmost modal
      const lastModal = prev[prev.length - 1];
      if (lastModal?.resolve) {
        lastModal.resolve(result);
      }
      return newStack;
    });
  };

  const closeAllModals = () => {
    setModalStack((prev) => {
      prev.forEach((modal) => modal.resolve(undefined));
      return [];
    });
  };

  return (
    <ModalContext.Provider value={{ showModal, closeModal, closeAllModals }}>
      {children}
      {modalStack.map((modal, index) => (
        <ModalBackdrop
          key={index}
          className="fixed inset-0 bg-black/25 flex items-center justify-center"
          style={{ zIndex: 50 + index * 10 }} // Incremental z-index for stacking
          onClick={(e) => {
            if (
              refs.current[index] &&
              !refs.current[index]?.contains(e.target as Node) &&
              shouldHideModalOnOutsideClick
            ) {
              closeModal(); // Close only the topmost modal
            }
          }}
        >
          <ModalWrapper
            ref={(el) => {
              refs.current[index] = el;
            }}
            style={{
              transform: `scale(${1 - index * 0.05})`, // Slightly scale down lower modals
              opacity: index < modalStack.length - 1 ? 0.8 : 1, // Dim lower modals
            }}
            className={modal.className}
          >
            {modal.content}
          </ModalWrapper>
        </ModalBackdrop>
      ))}
    </ModalContext.Provider>
  );
}

export function ModalWrapper(props: ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "bg-white rounded-2xl max-w-[628px] w-full mx-4 shadow-lg",
        props.className,
      )}
    />
  );
}

export function ModalBackdrop(props: ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        // Without a z-index, anything later in the DOM that forms its own
        // stacking context (opacity, filters, blend modes, positioning)
        // paints on top of the backdrop.
        "fixed inset-0 z-50 bg-black/25 flex items-center justify-center",
        props.className,
      )}
    />
  );
}

export function useMuiModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useMuiModal must be used within a ModalProvider");
  }
  return context;
}

interface ModalProps {
  children: ReactNode;
  onClick?: () => void;
}

export function Modal({ children, ...props }: ModalProps) {
  return (
    <div className="overflow-hidden" onClick={props.onClick}>
      {children}
    </div>
  );
}

export function ModalTitle({
  children,
  className,
}: {
  children: ReactNode;
} & Pick<ComponentProps<"h2">, "className">) {
  return (
    <div className="relative bg-[url('/dialog-header.svg')] bg-cover bg-bottom min-h-[200px] rounded-t-2xl grid items-center">
      <h2
        className={cn(
          `font-fancy text-[64px] pt-4 pb-8 text-white text-center mb-4`,
          className,
        )}
      >
        {children}
      </h2>
    </div>
  );
}

export function ModalContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "text-[20px] text-center flex flex-col gap-5 items-center py-8 px-22",
        className,
      )}
    >
      {children}
    </div>
  );
}
